import { useMemo } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  DatePicker,
  Drawer,
  Input,
  Select,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Gender } from '@/common/models/person'
import { UserType } from '@/common/models/user'
import { capitalizeFirstLetter } from '@/common/utils/capitalizeFirstLetter'
import { formatDate } from '@/common/utils/formatDate'
import { getLocale } from '@/common/utils/getLocale'
import { translateGender } from '@/common/utils/translateGender'
import { translateUserType } from '@/common/utils/translateUserType'
import { useFilterApplicationAccountUser } from '../hooks/useFilterApplicationAccountUser'
import {
  FilterUserApplicationAccountUserSchema,
  FilterUsersAccessForm,
} from '../schemas/FilterUserApplicationAccountUserSchema'

export function DrawerFilterApplicationAccountUser() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-filter-application-account-user')
  const [_, setFilters] = useFilterApplicationAccountUser()
  const hookForm = useForm<FilterUsersAccessForm>({
    resolver: zodResolver(FilterUserApplicationAccountUserSchema()),
    defaultValues: {
      freeSearch: null,
      userType: null,
      gender: null,
      birthMonth: null,
      birthYear: null,
      startCreated: null,
      endCreated: null,
    },
  })

  const handleSubmit = (data: FilterUsersAccessForm) => {
    setFilters(data)
    close()
  }

  const USER_TYPE_OPTIONS = Object.values(UserType).map((type) => ({
    label: translateUserType(t, type),
    value: type,
  }))

  const GENDER_OPTIONS = Object.values(Gender).map((gender) => ({
    label: translateGender(t, gender),
    value: gender,
  }))

  const BIRTH_MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
    label: capitalizeFirstLetter(formatDate(new Date(2024, i, 1), 'MMMM')),
    value: (i + 1).toString(),
  }))

  const BIRTH_YEAR_OPTIONS = useMemo(
    () =>
      Array.from({ length: 100 }, (_, i) => ({
        label: (new Date().getFullYear() - i).toString(),
        value: (new Date().getFullYear() - i).toString(),
      })),
    [],
  )

  return (
    <Drawer.Root id="drawer-filter-application-account-user">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:access.drawer_filter.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:access.drawer_filter.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <Input.Icon
              icon={<Search />}
              name="freeSearch"
              label={t('sso:access.drawer_filter.fields.free_search.label')}
              placeholder={t(
                'sso:access.drawer_filter.fields.free_search.placeholder',
              )}
            />

            <Select.Single
              name="userType"
              label={t('sso:access.drawer_filter.fields.user_type.label')}
              placeholder={t(
                'sso:access.drawer_filter.fields.user_type.placeholder',
              )}
              options={USER_TYPE_OPTIONS}
            />

            <Select.Single
              name="gender"
              label={t('sso:access.drawer_filter.fields.gender.label')}
              placeholder={t(
                'sso:access.drawer_filter.fields.gender.placeholder',
              )}
              options={GENDER_OPTIONS}
            />

            <div className="flex justify-between gap-4">
              <Select.Single
                name="birthMonth"
                label={t('sso:access.drawer_filter.fields.birth_month.label')}
                placeholder={t(
                  'sso:access.drawer_filter.fields.birth_month.placeholder',
                )}
                options={BIRTH_MONTH_OPTIONS}
              />

              <Select.Single
                name="birthYear"
                label={t('sso:access.drawer_filter.fields.birth_year.label')}
                placeholder={t(
                  'sso:access.drawer_filter.fields.birth_year.placeholder',
                )}
                options={BIRTH_YEAR_OPTIONS}
              />
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm text-base-800 ">
                {t('sso:access.drawer_filter.fields.start_created.label')}
              </span>

              <div className="flex justify-between gap-4">
                <DatePicker.Single
                  name="startCreated"
                  locale={getLocale()}
                  placeholder={t(
                    'sso:access.drawer_filter.fields.start_created.placeholder',
                  )}
                />

                <DatePicker.Single
                  name="endCreated"
                  locale={getLocale()}
                  placeholder={t(
                    'sso:access.drawer_filter.fields.end_created.placeholder',
                  )}
                  disabled={!hookForm.watch('startCreated')}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-between gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={close}
            type="button"
          >
            {t('sso:access.drawer_filter.footer.button_close.label')}
          </Button.Root>

          <div className="flex items-center gap-4">
            <Button.Root
              variant="secondary"
              color="danger"
              onClick={() => hookForm.reset()}
            >
              {t('sso:access.drawer_filter.footer.reset_button.label')}
            </Button.Root>

            <Button.Root
              variant="primary"
              color="brand"
              onClick={hookForm.handleSubmit(handleSubmit)}
              type="submit"
            >
              {t('sso:access.drawer_filter.footer.submit_button.label')}
            </Button.Root>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
